import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { analyticsEvents, ROUTES } from "../../../../db/models/const";
import { useHistory } from "react-router";
import { CompanyInterface } from "../../../../db/models/company/company";
import "./style.scss";
import { getCompanyById } from "../../../../api/company";
import { useForm } from "react-hook-form";
import {
	checkIfEmailNotInUse,
	signupAfolStudent,
} from "../../../../api/auth";
import { IconButton } from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { PasswordStrength } from "../../../../components/PasswordStrength/main";
import Button from "../../../../components/Common/Button";
import { Alert, Col, Container, Row } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import stepsLogo from "../../../../assets/logo-and-text.svg";
import CloseIcon from "@material-ui/icons/Close";
import {passwordPolicyRegex} from "../../../../helpers/auth";

interface PropsInterface {}

interface FormInterface {
	email: string;
	password: string;
	confirmPassword: string;
}

const SignupStudentCompanyBounded: React.FC<PropsInterface> = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const [company, setCompany] = useState<CompanyInterface | null>();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<boolean>(false);
	const [signupErrorMessage, setSignupErrorMessage] = useState<string>();
	const { register, handleSubmit, errors } = useForm();
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const MIN_PSW_LENGTH = 8;

	useEffect(() => {
		const query = new URLSearchParams(location.search);
		const referral = query.get("referral");
		if (referral) {
			setLoading(true);
			getCompanyById(referral).then((company) => {
				setLoading(false);
				if (company) {
					setCompany(company);
				} else setError(true);
			});
		} else {
			setLoading(false);
			setError(true);
		}
	}, []);

	const handlePasswordVisiblity = () => {
		setShowPassword(!showPassword);
	};

	const handleConfirmPasswordVisiblity = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const handleEmailChange = async (email: string) => {
		if (!email || !email.includes("@")) return;
		const notInUse = await checkIfEmailNotInUse(email);
		return notInUse;
	};

	const onRegister = (form: FormInterface) => {
		if (!company?.id) return;

		const email = form.email;
		const password = form.password;

		setLoading(true);
		signupAfolStudent(email, password, company.id)
			.then(async () => {
				setLoading(false);
				return;
			})
			.catch((error) => {
				const { code, message } = error;
				console.log(code);
				console.log(message);
				if (code === "functions/not-found") 
					setSignupErrorMessage(
						"Nessun utente trovato. Controlla la mail e riprova."
					);
				
				else
					setSignupErrorMessage(
						"Errore. Riprova."
					);
				
				setLoading(false);
			});
	};

	const handleGoTo = (path) => {
		history.push(path);
	};

	const goTo = (to) => {
		history.push(to);
	};

	return (
		<section className="signupStudentCompanyBounded">
			<Container>
				<Row className="justify-content-center">
					<Col md={6}>
						<h2>Iscriviti</h2>
						{company && (
							<div className="signupStudentCompanyBounded__content card">
								<Row className="justify-content-center align-items-center">
									<Col xs={4}>
										<Image rounded src={stepsLogo} fluid />
									</Col>
									<Col
										xs="auto"
										className="signupStudentCompanyBounded__logoDivider"
									>
										<CloseIcon />
									</Col>
									<Col xs={4} >
										<Image
											rounded
											src={company.logoUrl}
											fluid 
										/>
									</Col>
								</Row>
								<form onSubmit={handleSubmit(onRegister)}>
									<fieldset disabled={loading}>
										<div className="element">
											<div className="label">
												{t("signup_email_label") + "*"}
											</div>
											<input
												type="email"
												className={errors.email ? "input--error" : ""}
												placeholder={t("signup_email_ph")}
												name="email"
												ref={register({
													required: true,
													validate: handleEmailChange,
												})}
											/>
											{errors.email?.type === "required" && (
												<span className="error">
													{t("signup_email_error_required")}
												</span>
											)}
											{errors.email?.type === "validate" && (
												<span className="error">
													{t("signup_email_error_inUse")}
												</span>
											)}
										</div>
										<div className="element element--password">
											<div className="label">{t("signup_psw_label") + "*"}</div>
											<div
												className={`inputWrapper ${
													errors.password ? "input--error" : ""
												}`}
											>
												<input
													type={showPassword ? "text" : "password"}
													placeholder={t("signup_psw_ph")}
													onChange={(event) => setPassword(event.target.value)}
													name="password"
													ref={register({
														required: true,
														minLength: MIN_PSW_LENGTH,
														pattern:
															passwordPolicyRegex(), //Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character
													})}
												/>
												<IconButton onClick={handlePasswordVisiblity}>
													{!showPassword && <VisibilityIcon />}
													{showPassword && <VisibilityOffIcon />}
												</IconButton>
											</div>
											<PasswordStrength password={password} />
											<p className="format">{t("signup_psw_format")}</p>
											{errors.password?.type === "required" && (
												<span className="error">
													{t("signup_psw_error_required")}
												</span>
											)}
											{errors.password?.type === "minLength" && (
												<span className="error">
													{t("signup_psw_error_minLength")}
												</span>
											)}
											{errors.password?.type === "pattern" && (
												<span className="error">
													{t("signup_psw_error_pattern")}
												</span>
											)}
										</div>
										<div className="element">
											<div className="label">
												{t("signup_confirmPsw_label") + "*"}
											</div>
											<div
												className={`inputWrapper ${
													errors.confirmPassword ? "input--error" : ""
												}`}
											>
												<input
													type={showConfirmPassword ? "text" : "password"}
													placeholder={t("signup_confirmPsw_ph")}
													name="confirmPassword"
													ref={register({
														required: true,
														validate: {
															equalsPassword: (value) => value === password,
														},
													})}
												/>
												<IconButton onClick={handleConfirmPasswordVisiblity}>
													{!showConfirmPassword && <VisibilityIcon />}
													{showConfirmPassword && <VisibilityOffIcon />}
												</IconButton>
											</div>
											{errors.confirmPassword && (
												<span className="error">
													{t("signup_confirmPsw_error")}
												</span>
											)}
										</div>
										<div className="checkboxList">
											<div className="checkbox first">
												<input
													type="checkbox"
													name="privacy"
													ref={register({ required: true })}
													className={errors.privacy ? "input--error" : ""}
												></input>
												<p>
													{t("signup_privacy_text") + " "}
													<span
														className="link"
														onClick={() => goTo(ROUTES.PRIVACY)}
													>
														{t("signup_privacy_link")}
													</span>
												</p>
											</div>
											<div className="checkbox">
												<input
													type="checkbox"
													name="terms"
													ref={register({ required: true })}
													className={errors.terms ? "input--error" : ""}
												></input>
												<p>
													{t("signup_terms_text") + " "}
													<span
														className="link"
														onClick={() => goTo(ROUTES.TERMS)}
													>
														{t("signup_terms_link")}
													</span>
												</p>
											</div>
											{(errors.privacy || errors.terms) && (
												<span className="error">{t("signup_legal_error")}</span>
											)}
										</div>
										<Button type="submit" backgroundColor="#3392E8" borderColor="#3392E8" filled loading={loading}>
											{t("signup_button")}
										</Button>
									</fieldset>
								</form>
								{signupErrorMessage && (
									<Alert variant="danger">{signupErrorMessage}</Alert>
								)}
								<p
									className="signupStudentCompanyBounded__goToLogin"
									onClick={() => handleGoTo(ROUTES.LOGIN)}
								>
									{t("signup_goToLogin_question")}
									<span className="link">{t("signup_goToLogin_answer")}</span>
								</p>
							</div>
						)}
						{error && <p>{t("signup_student_company_error")}</p>}
					</Col>
				</Row>
			</Container>
		</section>
	);
};

export default SignupStudentCompanyBounded;
